/**
 * Classrooms, and who teaches what in them — the screen both consoles render.
 *
 * A school admin runs it against their own school; a platform super admin runs
 * it against CWY's own workspace. The job is identical, so the component is one
 * component: a class is a board and a grade, and a teacher reaches it, and
 * therefore its papers and its results, by holding a subject IN it. The two
 * differ in exactly two things, and both are props — which API the writes go to,
 * and whether sections exist at all.
 *
 * The classroom CODE is shown prominently because it is not decoration: it is
 * what a roster import types in its second column.
 */
import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { apiGet, apiPost, apiPut } from '@/lib/api';
import { queryClient } from '@/lib/queryClient';
import { gradeLabel, classroomCode, classroomName } from '@cwy/config/contracts';
import type {
  Board,
  ClassroomDTO,
  CurriculumSubjectDTO,
  CurriculumTreeDTO,
  Grade,
} from '@cwy/config/contracts';
import {
  Button,
  Card,
  Field,
  Input,
  Select,
  StudentPage,
  StudentPageHeader,
} from '@/components/ds';
import { QueryState } from '@/components/admin/QueryState';
import { ClassBooksPanel, ClassStudentsPanel } from '@/components/admin/ClassroomPanels';

/** Someone who can be given a subject here. Both consoles reduce their own staff list to this. */
export interface AssignableTeacher {
  membershipId: string;
  name: string;
  /** False for a counsellor: a class teacher manages a roster, which they do not. */
  canBeClassTeacher?: boolean;
}

export interface ClassroomsManagerProps {
  /** Page copy. The two consoles describe the same screen to different readers. */
  title: string;
  subtitle: string;
  /** Where classroom writes go — `/tenant-admin/classrooms` or `/platform/classrooms`. */
  endpoint: string;
  /** The React Query key the two lists share, so a write invalidates the right one. */
  queryKey: readonly unknown[];
  /** The board·grade pairs this workspace runs. A cohort it has not selected gets no class. */
  cohorts: { board: Board; grade: Grade }[];
  cohortsLoaded: boolean;
  teachers: AssignableTeacher[];
  /**
   * Whether a class can be divided into sections. False for the first-party
   * workspace: CWY's own students are one cohort per board·grade, so a section
   * there would be a division of nothing.
   */
  allowSection?: boolean;
  /**
   * The topics this workspace runs, or null for the whole tree. A school picks
   * subjects and units only from what it selected under Curriculum — offering
   * an unticked subject here would scope a teacher to work no student sees.
   */
  offeredChapterIds?: ReadonlySet<string> | null;
  /**
   * Whether classes have a class teacher here — a school's do. Writes go to
   * `${endpoint}/:id/class-teacher`.
   */
  classTeachers?: boolean;
  /**
   * Whether each card also opens its Students and Books — a school's do; CWY's
   * own workspace has no roster to list and no shelf to stock. Both read
   * `/tenant-admin` (components/admin/ClassroomPanels).
   */
  classPanels?: boolean;
}

/** Which of a card's panels is open. */
type CardPanel = 'teachers' | 'students' | 'books';

/**
 * A cohort's subjects, narrowed to what the workspace offers.
 *
 * A subject is offered when any of its topics is, and a unit likewise — the
 * same derivation the server's `offered*Where` uses. The curriculum tree is a
 * syllabus readable by any signed-in caller, so one query serves both consoles.
 *
 * `academicYearId` is the CLASS's year. A school runs its own year, which need
 * not be the platform's active one (it starts the next when it is ready), and
 * the class's subjects are the only ones the server accepts for it — a tree of
 * another year would offer subjects that are all silently rejected.
 */
export function useOfferedSubjects(
  board: Board | null,
  grade: Grade | null,
  offeredChapterIds: ReadonlySet<string> | null | undefined,
  academicYearId: string | null = null,
) {
  const tree = useQuery({
    queryKey: ['curriculum', 'tree', board, grade, academicYearId],
    enabled: Boolean(board && grade),
    queryFn: () =>
      apiGet<CurriculumTreeDTO>(
        `/curriculum/tree?board=${board}&grade=${grade}` +
          (academicYearId ? `&academicYearId=${academicYearId}` : ''),
      ),
  });
  const subjects = useMemo<CurriculumSubjectDTO[]>(() => {
    const all = tree.data?.subjects ?? [];
    if (!offeredChapterIds) return all;
    return all
      .map((subject) => ({
        ...subject,
        units: subject.units.filter((unit) =>
          unit.chapters.some((chapter) => offeredChapterIds.has(chapter.id)),
        ),
      }))
      .filter((subject) => subject.units.length > 0);
  }, [tree.data, offeredChapterIds]);
  return { subjects, isPending: tree.isPending };
}

/** One subject in one class, and all of it or some of its units. */
export interface SubjectUnitsValue {
  subjectId: string;
  /** Null is the whole subject. */
  unitIds: string[] | null;
}

/**
 * Subject, then "All units (whole subject)" OR a selection of units.
 *
 * "All units" is its own choice rather than every unit ticked: it has to keep
 * meaning all of them when next year's syllabus adds one, and an enumerated set
 * silently would not (AGENTS.md rule 5). Shared by the classroom card and the
 * staff page, so the two editors offer exactly the same choice.
 */
export function SubjectUnitsPicker({
  subjects,
  value,
  onChange,
  disabled,
}: {
  subjects: CurriculumSubjectDTO[];
  value: SubjectUnitsValue;
  onChange: (next: SubjectUnitsValue) => void;
  disabled?: boolean;
}) {
  const subject = subjects.find((row) => row.id === value.subjectId) ?? null;
  const whole = value.unitIds === null;
  const chosen = new Set(value.unitIds ?? []);

  return (
    <div className="flex min-w-[220px] flex-1 flex-col gap-2">
      <Field label="Subject">
        {({ id }) => (
          <Select
            id={id}
            value={value.subjectId}
            disabled={disabled}
            onChange={(e) => onChange({ subjectId: e.target.value, unitIds: null })}
          >
            <option value="">Choose…</option>
            {subjects.map((row) => (
              <option key={row.id} value={row.id}>
                {row.name}
              </option>
            ))}
          </Select>
        )}
      </Field>
      {subject && (
        <div className="flex flex-col gap-1.5">
          <label className="flex items-center gap-2 text-fs-13">
            <input
              type="checkbox"
              checked={whole}
              disabled={disabled}
              onChange={(e) =>
                onChange({ subjectId: value.subjectId, unitIds: e.target.checked ? null : [] })
              }
            />
            <span className="font-semibold text-ink">All units (whole subject)</span>
          </label>
          {!whole && (
            <div
              className="flex flex-col gap-1 rounded-lg border border-line px-3 py-2"
              role="group"
              aria-label={`Units of ${subject.name}`}
            >
              {subject.units.map((unit) => (
                <label key={unit.id} className="flex items-center gap-2 text-fs-13 text-ink">
                  <input
                    type="checkbox"
                    checked={chosen.has(unit.id)}
                    disabled={disabled}
                    onChange={(e) => {
                      const next = new Set(chosen);
                      if (e.target.checked) next.add(unit.id);
                      else next.delete(unit.id);
                      onChange({
                        subjectId: value.subjectId,
                        // Kept in syllabus order, not click order.
                        unitIds: subject.units.map((u) => u.id).filter((uid) => next.has(uid)),
                      });
                    }}
                  />
                  {unit.name}
                </label>
              ))}
              {chosen.size === 0 && (
                <span className="text-xs text-ink-soft">Tick at least one unit.</span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Is a picker value complete enough to save? */
export function isCompleteSubjectUnits(value: SubjectUnitsValue): boolean {
  return Boolean(value.subjectId) && (value.unitIds === null || value.unitIds.length > 0);
}

export function ClassroomsManager({
  title,
  subtitle,
  endpoint,
  queryKey,
  cohorts,
  cohortsLoaded,
  teachers,
  allowSection = true,
  offeredChapterIds = null,
  classTeachers = false,
  classPanels = false,
}: ClassroomsManagerProps) {
  const classrooms = useQuery({
    queryKey,
    queryFn: () => apiGet<ClassroomDTO[]>(endpoint),
  });

  const [showForm, setShowForm] = useState(false);
  const [cohort, setCohort] = useState('');
  const [section, setSection] = useState('');
  const [error, setError] = useState('');
  // One card, one panel open at a time — this is a list, not a dashboard.
  const [expanded, setExpanded] = useState<{ id: string; panel: CardPanel } | null>(null);
  const panels: { panel: CardPanel; label: string }[] = classPanels
    ? [
        { panel: 'students', label: 'Students' },
        { panel: 'books', label: 'Books' },
        { panel: 'teachers', label: 'Teachers' },
      ]
    : [{ panel: 'teachers', label: 'Teachers' }];
  const toggle = (id: string, panel: CardPanel) =>
    setExpanded((open) => (open?.id === id && open.panel === panel ? null : { id, panel }));

  const chosen =
    cohorts.find((row) => `${row.board}:${row.grade}` === cohort) ?? cohorts[0] ?? null;
  const newSection = allowSection ? section.trim() : '';

  const createClassroom = useMutation({
    mutationFn: () =>
      apiPost<ClassroomDTO>(endpoint, {
        board: chosen!.board,
        grade: chosen!.grade,
        ...(newSection ? { section: newSection } : {}),
      }),
    onSuccess: () => {
      setShowForm(false);
      setSection('');
      void queryClient.invalidateQueries({ queryKey });
    },
    onError: (err: Error) => setError(err.message),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError('');
    if (chosen) createClassroom.mutate();
  };

  return (
    <StudentPage>
      <StudentPageHeader
        title={title}
        subtitle={subtitle}
        action={
          <Button disabled={cohorts.length === 0} onClick={() => setShowForm((v) => !v)}>
            {showForm ? 'Cancel' : 'Add a classroom'}
          </Button>
        }
      />

      {error && <p className="text-sm text-danger">{error}</p>}

      {cohorts.length === 0 && cohortsLoaded && (
        <Card title="Choose the curriculum first">
          <p className="text-sm text-ink-soft">
            A classroom needs a board and a grade this workspace actually runs. Tick them under
            Curriculum, then come back.
          </p>
        </Card>
      )}

      {showForm && chosen && (
        <Card title="Add a classroom">
          <form className="flex flex-col gap-4" onSubmit={submit} noValidate>
            <div className="flex flex-wrap gap-3">
              <Field label="Board and grade" className="min-w-[200px] flex-1" required>
                {({ id }) => (
                  <Select
                    id={id}
                    value={`${chosen.board}:${chosen.grade}`}
                    onChange={(e) => setCohort(e.target.value)}
                  >
                    {cohorts.map((row) => (
                      <option key={`${row.board}:${row.grade}`} value={`${row.board}:${row.grade}`}>
                        {row.board} · Grade {gradeLabel(row.grade)}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              {/* Disabled rather than absent where sections do not apply, so the
                  two consoles stay the same screen and the reason is stated. */}
              <Field
                label="Section (optional)"
                className="min-w-[140px] flex-1"
                hint={allowSection ? undefined : 'One class per board and grade here.'}
              >
                {({ id }) => (
                  <Input
                    id={id}
                    value={allowSection ? section : ''}
                    disabled={!allowSection}
                    maxLength={20}
                    placeholder="A"
                    onChange={(e) => setSection(e.target.value)}
                  />
                )}
              </Field>
            </div>
            <p className="text-xs text-ink-soft">
              This will be{' '}
              <strong className="text-forest">
                {classroomName(chosen.board, chosen.grade, newSection || null)}
              </strong>
              , code{' '}
              <code className="rounded bg-paper-dim px-1 py-0.5 text-fs-11.5 font-semibold text-forest">
                {classroomCode(chosen.board, chosen.grade, newSection || null)}
              </code>
              . The code is what a roster import names.
            </p>
            <div>
              <Button type="submit" disabled={createClassroom.isPending}>
                {createClassroom.isPending ? 'Creating…' : 'Create classroom'}
              </Button>
            </div>
          </form>
        </Card>
      )}

      <QueryState
        isPending={classrooms.isPending}
        error={classrooms.error}
        isEmpty={classrooms.data?.length === 0}
        empty={
          <p className="text-sm text-ink-soft">
            No classrooms yet. Create one, then assign a teacher their subjects — that is what gives
            a teacher access to their students’ work.
          </p>
        }
      >
        <div className="flex flex-col gap-2.5">
          {classrooms.data?.map((classroom) => (
            <Card key={classroom.id} padding="14px 16px">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="truncate text-base font-bold text-forest">
                    {classroom.name}{' '}
                    <code className="ml-1 rounded bg-paper-dim px-1.5 py-0.5 text-fs-11.5 font-semibold text-ink-soft">
                      {classroom.code}
                    </code>
                  </h3>
                  <p className="mt-0.5 text-xs text-ink-soft">
                    {classroom.studentCount} students · {classroom.teacherCount} teachers
                    {classroom.academicYear && ` · ${classroom.academicYear.label}`}
                    {classTeachers &&
                      ` · Class teacher: ${classroom.classTeacher?.name ?? 'not set'}`}
                  </p>
                </div>
                <div className="flex flex-none flex-wrap items-center gap-2">
                  {panels.map(({ panel, label }) => {
                    const open = expanded?.id === classroom.id && expanded.panel === panel;
                    return (
                      <Button
                        key={panel}
                        variant={open ? 'primary' : 'secondary'}
                        aria-expanded={open}
                        onClick={() => toggle(classroom.id, panel)}
                      >
                        {open && panels.length === 1 ? 'Close' : label}
                      </Button>
                    );
                  })}
                </div>
              </div>

              {expanded?.id === classroom.id && expanded.panel === 'students' && (
                <ClassStudentsPanel classroom={classroom} />
              )}
              {expanded?.id === classroom.id && expanded.panel === 'books' && (
                <ClassBooksPanel classroom={classroom} />
              )}
              {expanded?.id === classroom.id && expanded.panel === 'teachers' && (
                <>
                  {classTeachers && (
                    <ClassTeacherControl
                      classroom={classroom}
                      endpoint={endpoint}
                      queryKey={queryKey}
                      teachers={teachers}
                      onError={setError}
                    />
                  )}
                  <TeacherScopeEditor
                    classroom={classroom}
                    endpoint={endpoint}
                    queryKey={queryKey}
                    teachers={teachers}
                    offeredChapterIds={offeredChapterIds}
                    onError={setError}
                  />
                </>
              )}
            </Card>
          ))}
        </div>
      </QueryState>
    </StudentPage>
  );
}

/**
 * Who looks after this class. A relationship to this room, not a role: the
 * class teacher reads every subject's work here and manages its roster, and
 * nothing changes for them in the class next door.
 */
function ClassTeacherControl({
  classroom,
  endpoint,
  queryKey,
  teachers,
  onError,
}: {
  classroom: ClassroomDTO;
  endpoint: string;
  queryKey: readonly unknown[];
  teachers: AssignableTeacher[];
  onError: (message: string) => void;
}) {
  const save = useMutation({
    mutationFn: (membershipId: string | null) =>
      apiPut(`${endpoint}/${classroom.id}/class-teacher`, { membershipId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey });
      // The staff list shows "Class teacher · 10-A" too.
      void queryClient.invalidateQueries({ queryKey: ['tenant-admin', 'staff'] });
    },
    onError: (err: Error) => onError(err.message),
  });

  return (
    <div className="mt-4 flex flex-wrap items-end gap-2.5 border-t border-line pt-4">
      <Field
        label="Class teacher"
        className="min-w-[220px] flex-1"
        hint="Reads every subject in this class and looks after its roster — adding students and resetting their passwords."
      >
        {({ id }) => (
          <Select
            id={id}
            value={classroom.classTeacher?.membershipId ?? ''}
            disabled={save.isPending}
            onChange={(e) => {
              onError('');
              save.mutate(e.target.value || null);
            }}
          >
            <option value="">No class teacher</option>
            {teachers
              .filter((member) => member.canBeClassTeacher !== false)
              .map((member) => (
                <option key={member.membershipId} value={member.membershipId}>
                  {member.name}
                </option>
              ))}
          </Select>
        )}
      </Field>
    </div>
  );
}

/**
 * Who teaches this class, and what.
 *
 * One row per (teacher, subject, unit). "All units" is a distinct choice rather
 * than every unit ticked — it has to keep meaning all of them when next year's
 * syllabus adds one, and an enumerated set silently would not. Choosing several
 * units sends one row per unit; the server normalizes the lot.
 */
function TeacherScopeEditor({
  classroom,
  endpoint,
  queryKey,
  teachers,
  offeredChapterIds,
  onError,
}: {
  classroom: ClassroomDTO;
  endpoint: string;
  queryKey: readonly unknown[];
  teachers: AssignableTeacher[];
  offeredChapterIds: ReadonlySet<string> | null;
  onError: (message: string) => void;
}) {
  const { subjects } = useOfferedSubjects(
    classroom.board,
    classroom.grade,
    offeredChapterIds,
    classroom.academicYear?.id ?? null,
  );

  const [membershipId, setMembershipId] = useState('');
  const [pick, setPick] = useState<SubjectUnitsValue>({ subjectId: '', unitIds: null });

  const save = useMutation({
    mutationFn: (scopes: { membershipId: string; subjectId: string; unitId: string | null }[]) =>
      apiPut(`${endpoint}/${classroom.id}/teachers`, { scopes }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey });
      void queryClient.invalidateQueries({ queryKey: ['tenant-admin', 'staff'] });
    },
    onError: (err: Error) => onError(err.message),
  });

  // Replace-in-full on every change: the list on screen IS the intended set, so
  // add and remove both send the whole thing rather than a diff the server would
  // have to reconcile against a page someone left open.
  const current = classroom.scope.map((row) => ({
    membershipId: row.membershipId,
    subjectId: row.subjectId,
    unitId: row.unitId,
  }));

  const add = () => {
    if (!membershipId || !isCompleteSubjectUnits(pick)) return;
    onError('');
    const added =
      pick.unitIds === null
        ? [{ membershipId, subjectId: pick.subjectId, unitId: null }]
        : pick.unitIds.map((unitId) => ({ membershipId, subjectId: pick.subjectId, unitId }));
    save.mutate([...current, ...added]);
    setPick({ subjectId: pick.subjectId, unitIds: null });
  };

  return (
    <div className="mt-4 flex flex-col gap-4 border-t border-line pt-4">
      {classroom.scope.length === 0 ? (
        <p className="text-sm text-ink-soft">
          Nobody teaches this class yet. A teacher sees a class only once they hold a subject in it
          {classroom.classTeacher ? ', or are its class teacher' : ''}.
        </p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {classroom.scope.map((row) => (
            <li
              key={`${row.membershipId}:${row.subjectId}:${row.unitId ?? 'all'}`}
              className="flex items-center justify-between gap-3 rounded-lg border border-line px-3 py-2"
            >
              <span className="min-w-0 truncate text-fs-13">
                <strong className="font-semibold text-forest">{row.teacherName}</strong>
                <span className="text-ink-soft">
                  {' '}
                  · {row.subjectName} · {row.unitName ?? 'All units'}
                </span>
              </span>
              <button
                type="button"
                className="shrink-0 text-xs font-semibold text-danger"
                disabled={save.isPending}
                onClick={() => {
                  onError('');
                  save.mutate(
                    current.filter(
                      (each) =>
                        !(
                          each.membershipId === row.membershipId &&
                          each.subjectId === row.subjectId &&
                          each.unitId === row.unitId
                        ),
                    ),
                  );
                }}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      {teachers.length === 0 ? (
        <p className="text-sm text-ink-soft">No teachers yet — add them under Staff first.</p>
      ) : (
        <div className="flex flex-wrap items-start gap-2.5">
          <Field label="Teacher" className="min-w-[160px] flex-1">
            {({ id }) => (
              <Select
                id={id}
                value={membershipId}
                onChange={(e) => setMembershipId(e.target.value)}
              >
                <option value="">Choose…</option>
                {teachers.map((member) => (
                  <option key={member.membershipId} value={member.membershipId}>
                    {member.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <SubjectUnitsPicker subjects={subjects} value={pick} onChange={setPick} />
          <div className="pt-6">
            <Button
              variant="secondary"
              disabled={!membershipId || !isCompleteSubjectUnits(pick) || save.isPending}
              onClick={add}
            >
              Assign
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
